<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { game } from '$stores/game.svelte';
  import { pair, fmtCooldown } from '$stores/pair.svelte';
  import { profile } from '$stores/profile.svelte';
  import { generateNickname } from '$data/nicknames';
  import TwinPicker from './TwinPicker.svelte';
  import Mascot from './Mascot.svelte';

  let inputEl: HTMLInputElement;
  /** Tick once a minute so the cooldown countdown stays fresh. */
  let cooldownTick = $state(0);
  let cooldownTimer: ReturnType<typeof setInterval> | null = null;

  onMount(() => {
    cooldownTimer = setInterval(() => (cooldownTick += 1), 60_000);
  });
  onDestroy(() => {
    if (cooldownTimer) clearInterval(cooldownTimer);
  });

  /** Tracked manually so reactivity hits the value in the template. */
  const cooldownMs = $derived.by(() => {
    void cooldownTick;
    return pair.current ? pair.cooldownRemainingMs(pair.current.pairId) : 0;
  });

  const ctaLabel = $derived.by(() => {
    if (cooldownMs > 0) {
      return `Next pulse in ${fmtCooldown(cooldownMs)}`;
    }
    if (pair.isBonded) return `PRESS START · @${pair.friendHandle} →`;
    return 'PRESS START → SOLO';
  });

  /** Width of the XP fill bar as a percentage of the level span. */
  const levelFillPct = $derived.by(() => {
    const span = Math.max(1, game.xpForNext - game.xpForLevel);
    const filled = Math.max(0, game.xp - game.xpForLevel);
    return Math.min(100, (filled / span) * 100);
  });

  function shuffle() {
    if (inputEl) {
      inputEl.style.opacity = '0';
      setTimeout(() => {
        profile.setHandle(generateNickname());
        inputEl.style.opacity = '1';
      }, 150);
    } else {
      profile.setHandle(generateNickname());
    }
  }

  function onInput(e: Event) {
    profile.setHandle((e.target as HTMLInputElement).value);
  }

  function onBlur() {
    if (!profile.handle) profile.setHandle(generateNickname());
  }

  /** Fetch today's deterministic seed; fall back to non-seeded generation
   *  if the server is unreachable so the SPA stays playable offline. */
  async function fetchDailySeed(): Promise<{ seed?: number; id?: string }> {
    try {
      const res = await fetch('/api/pulse/seed', { cache: 'no-store' });
      if (!res.ok) return {};
      const data = (await res.json()) as { seed?: number; id?: string };
      return data;
    } catch {
      return {};
    }
  }

  async function start() {
    if (!profile.handle) profile.setHandle(generateNickname());
    // Bonded twins fetch the global daily seed so both sides share content.
    // Solo derives its own content seed from (today_UTC, category) — it
    // never hits the seed endpoint, keeping replays offline-friendly.
    if (pair.isBonded && cooldownMs <= 0) {
      const { seed } = await fetchDailySeed();
      game.startNewPulse({ seed, mode: 'bonded' });
    } else {
      game.startNewPulse({ mode: 'solo' });
    }
    game.goto('play');
  }
</script>

<section class="intro">
  <div class="intro-mark-static intro-mark-pair" aria-hidden="true">
    <Mascot size={80} state="idle" variant="primary" letter="A" />
    <Mascot size={80} state="idle" variant="twin" letter="D" />
  </div>

  <div class="intro-content">
    <h1 class="intro-headline">ARE <span class="hl-gold">YOU</span> TWINS?</h1>
    <p class="intro-sub">
      A 60-second daily pulse with your closest friend. Same prompt. Blind answers. One reveal.
    </p>

    <div class="intro-mark-floater" aria-hidden="true">
      <Mascot size={56} state="idle" variant="purple" letter="P" />
    </div>

    <div class="nickname-row">
      <div class="nickname-label">
        <span>YOUR HANDLE</span>
        <span class="nickname-label-hint">tap to edit</span>
      </div>
      <div class="nickname-field">
        <input
          bind:this={inputEl}
          value={profile.handle}
          oninput={onInput}
          type="text"
          maxlength="18"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          onblur={onBlur}
        />
        <button class="nickname-shuffle" type="button" aria-label="Shuffle handle" onclick={shuffle}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
        </button>
      </div>
    </div>

    <div class="twin-row">
      <div class="twin-label">
        <span>YOUR TWIN</span>
        {#if pair.active.length > 1}
          <span class="twin-label-hint">tap to switch</span>
        {/if}
      </div>
      <TwinPicker />
    </div>
  </div>

  <div class="intro-cta-wrap">
    <div class="level-card">
      <div class="level-card-row">
        <div class="level-pill">LVL {game.level} · {game.levelTitle}</div>
        <div class="level-text">{game.xp} / {game.xpForNext} XP</div>
      </div>
      <div class="level-bar"><div class="level-fill" style:width="{levelFillPct}%"></div></div>
      <div class="level-meta">
        <span>● TWIN STREAK</span>
        <span>{game.xpToNext} XP TO LVL {game.level + 1}</span>
      </div>
    </div>

    <button
      class="btn btn-hero intro-cta"
      class:solo={!pair.isBonded}
      onclick={start}
      disabled={pair.isBonded && cooldownMs > 0}
    >
      {ctaLabel}
    </button>
    {#if !pair.isBonded}
      <div class="intro-cta-mode">Echo mode · vs. yesterday-you</div>
    {/if}
    <div class="intro-cta-foot">No accounts · No tracking · No ads</div>
  </div>
</section>

<style>
  .intro {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(4px, 1.2dvh, 12px);
    padding: clamp(6px, 1.6dvh, 18px) var(--gutter) max(var(--safe-cta-bottom), 10px);
    text-align: center;
    overflow: hidden;
    animation: introFadeIn 400ms ease;
  }
  .intro-mark-static :global(.mascot) {
    width: clamp(56px, 14dvh, 80px);
    height: clamp(56px, 14dvh, 80px);
    --s: clamp(56px, 14dvh, 80px);
  }
  /* Pair layout: two tiles side-by-side with a slight overlap so they
     read as bonded twins rather than two unrelated icons. Using negative
     margin (gap can't go negative in flex) the right tile tucks under
     the left tile's hot-pink rim, mirroring the OG-card composition.
     Each tile keeps its own counter-rotation, so the overlap reads as
     two faces leaning toward each other. */
  .intro-mark-pair {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .intro-mark-pair :global(.mascot:first-child) {
    z-index: 2;
  }
  .intro-mark-pair :global(.mascot:last-child) {
    z-index: 1;
    margin-left: clamp(-22px, -4dvh, -14px);
  }
  @keyframes introFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  .intro-mark-static {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 0;
    will-change: transform;
    view-transition-name: brand-mark;
  }
  .intro-content {
    width: 100%;
    max-width: 320px;
    margin: 0;
    /* Anchor for the absolute `.intro-mark-floater` purple P. */
    position: relative;
  }
  .intro-headline {
    font-size: clamp(22px, 5.4vw, 32px);
    font-weight: 900;
    letter-spacing: -0.03em;
    line-height: 1.05;
    text-transform: uppercase;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  .hl-gold {
    color: var(--gold);
    text-shadow: 0 0 18px rgba(255, 216, 74, 0.45);
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  :global(body.brutal) .hl-gold {
    color: var(--text);
    text-shadow: none;
  }
  .intro-sub {
    font-size: 13px;
    color: var(--text-2);
    line-height: 1.5;
    margin-top: clamp(2px, 0.6dvh, 8px);
  }
  /* Decorative floater: a small purple `P` lettermark that sits in the
     negative space to the right of the handle row. Uses the existing
     `.variant-purple` Mascot variant so the bob/glow/sparkle vocabulary
     matches the A/D pair above the headline. `pointer-events: none`
     because the shuffle button (`.nickname-shuffle`) lives directly
     beneath it and must keep receiving taps. Hidden on the very
     narrowest phones to avoid colliding with the input field. */
  .intro-mark-floater {
    position: absolute;
    top: clamp(96px, 18dvh, 138px);
    right: clamp(-4px, 0vw, 6px);
    pointer-events: none;
    z-index: 2;
  }
  .intro-mark-floater :global(.mascot) {
    width: clamp(48px, 11dvh, 60px);
    height: clamp(48px, 11dvh, 60px);
    --s: clamp(48px, 11dvh, 60px);
  }
  @media (max-width: 360px) {
    .intro-mark-floater {
      display: none;
    }
  }

  .nickname-row {
    width: 100%;
    margin-top: clamp(6px, 1dvh, 14px);
  }
  .twin-row {
    width: 100%;
    margin-top: clamp(6px, 1dvh, 14px);
  }
  .twin-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
    margin-bottom: 6px;
  }
  .twin-label-hint {
    color: var(--text-3);
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: none;
  }
  .nickname-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
    margin-bottom: 6px;
  }
  .nickname-label-hint {
    color: var(--text-3);
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: none;
  }
  .nickname-field {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius);
    padding: 2px 4px 2px 12px;
    transition: var(--transition);
  }
  :global(body.brutal) .nickname-field {
    border-width: 2px;
  }
  .nickname-field:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.15);
  }
  :global(body.brutal) .nickname-field:focus-within {
    box-shadow: 3px 3px 0 0 var(--text);
  }
  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 16px;
    font-weight: 700;
    padding: 8px 0;
    letter-spacing: -0.01em;
    min-width: 0;
    transition: opacity 150ms ease;
  }
  :global(body.brutal) input {
    font-family: 'Courier New', monospace;
  }
  .nickname-shuffle {
    width: 38px;
    height: 38px;
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    display: grid;
    place-items: center;
    flex-shrink: 0;
    transition: var(--transition);
  }
  :global(body.brutal) .nickname-shuffle {
    border: 2px solid var(--text);
    background: var(--bg);
  }
  .nickname-shuffle:hover {
    background: rgba(0, 212, 255, 0.15);
  }
  :global(body.brutal) .nickname-shuffle:hover {
    background: var(--text);
  }
  .nickname-shuffle:hover svg {
    stroke: var(--primary);
  }
  :global(body.brutal) .nickname-shuffle:hover svg {
    stroke: var(--bg);
  }
  .nickname-shuffle:active {
    transform: rotate(-180deg);
  }
  .nickname-shuffle svg {
    width: 14px;
    height: 14px;
    stroke: var(--text-2);
    transition:
      transform 400ms ease,
      stroke var(--transition);
  }
  .intro-cta-wrap {
    width: 100%;
    max-width: 320px;
  }
  /* Width override only — button visuals come from app.css. */
  .intro-cta {
    width: 100%;
    text-align: center;
  }

  /* Solo variant: cool cyan → violet → hot gradient instead of the warm
     gold → hot → violet hero gradient. Kept inside .btn-hero's scope so
     all the structural rules (padding, gloss strip, press-translate)
     still apply — only the palette + offset shadow + glow change. The
     cyan accent here intentionally rhymes with the cyan "Echo mode"
     sub-label that sits directly under the button. */
  .intro-cta.solo {
    background: linear-gradient(
      135deg,
      var(--cyan) 0%,
      var(--violet) 55%,
      var(--hot) 100%
    );
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow:
      var(--depth-xl) var(--cyan-deep),
      var(--glow-cyan),
      0 0 30px rgba(167, 139, 250, 0.28);
  }
  .intro-cta.solo:active {
    box-shadow:
      0 0 0 0 var(--cyan-deep),
      var(--glow-cyan),
      0 0 30px rgba(167, 139, 250, 0.28);
  }
  :global(body.brutal) .intro-cta.solo {
    background: var(--text);
    color: var(--bg);
    border-color: var(--text);
    box-shadow: 4px 4px 0 0 var(--text);
  }
  :global(body.brutal) .intro-cta.solo:active {
    box-shadow: 2px 2px 0 0 var(--text);
  }

  /* ============ Level card ============ */
  .level-card {
    background: rgba(0, 0, 0, 0.5);
    border: 2px solid rgba(255, 216, 74, 0.4);
    border-radius: var(--r-md);
    padding: 8px 12px;
    margin-bottom: clamp(4px, 0.8dvh, 10px);
  }
  :global(body.brutal) .level-card {
    background: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .level-card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .level-pill {
    background: var(--gold);
    color: var(--text-on-light);
    font-weight: 900;
    font-size: 11px;
    padding: 4px 10px;
    border-radius: var(--r-pill);
    box-shadow: var(--depth-sm) var(--gold-deep);
    letter-spacing: 0.05em;
  }
  :global(body.brutal) .level-pill {
    background: var(--text);
    color: var(--bg);
    box-shadow: 2px 2px 0 0 var(--text);
  }
  .level-text {
    font-size: 11px;
    font-weight: 800;
    color: var(--gold);
  }
  :global(body.brutal) .level-text {
    color: var(--text);
  }
  .level-bar {
    height: 8px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: var(--r-pill);
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-top: 6px;
  }
  :global(body.brutal) .level-bar {
    background: var(--bg);
    border-color: var(--text);
  }
  .level-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--gold), var(--hot));
    box-shadow: 0 0 10px rgba(255, 216, 74, 0.6);
    border-radius: var(--r-pill);
    transition: width 1.2s var(--spring-bouncy);
  }
  :global(body.brutal) .level-fill {
    background: var(--text);
    box-shadow: none;
  }
  .level-meta {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
    font-size: 9px;
    font-weight: 800;
    color: var(--text-2);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .intro-cta-foot {
    text-align: center;
    font-size: 10px;
    color: var(--text-3);
    margin-top: clamp(2px, 0.5dvh, 10px);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 700;
  }
  .intro-cta-mode {
    text-align: center;
    font-size: 10px;
    color: var(--cyan);
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-weight: 800;
    text-shadow: 0 0 8px rgba(0, 212, 255, 0.35);
  }
  :global(body.brutal) .intro-cta-mode {
    color: var(--text);
    text-shadow: none;
  }
</style>
